/**
 * @file interpret/engine/webPlayer.ts
 * @brief Plays each translation and the "your turn" beep: web twin of `usePlayback`.
 *
 * @details
 * One AudioContext, created and resumed inside the Start click (`unlock()`), so
 * Safari and Chrome autoplay rules never block a translation that arrives later
 * with no user gesture. Audio is decoded and played through Web Audio rather than
 * an `<audio>` element because:
 * - `play()` resolves at the real end of the buffer (the phone's `waitForEnd`),
 *   which is when the mic gate may reopen. The phone learned on 14-07-2026 that a
 *   "looks finished" signal truncates the tail.
 * - The API's RAM-proxy URL needs a Bearer header, so it is fetched anyway.
 *
 * The beep is synthesised (no asset): a short soft tone, the cue the phone plays
 * before it reopens the mic.
 *
 * @author DES
 * @date 01-10-2026
 */

import type { TurnAudioSource } from "./endpoints";

export type PlayResult = { durationSec: number; interrupted: boolean };

/** Where the playing translation is: drives the word-by-word caption. */
export type PlaybackProgress = { currentSec: number; durationSec: number };

/** Chromium's `AudioContext.setSinkId` (output device), not yet in the TS lib. */
type SinkableContext = AudioContext & { setSinkId?: (id: string) => Promise<void> };

export class WebPlayer {
  private ctx: AudioContext | null = null;
  /** Call mode: a second context whose output is the call's mic device. */
  private callCtx: AudioContext | null = null;
  private hearSinkId = "";
  /** Call mode: louder here (see unlock), through a limiter. */
  private hereBoost = 1;
  private hereChain: { ctx: AudioContext; input: AudioNode } | null = null;
  private current: {
    node: AudioBufferSourceNode;
    finish: (interrupted: boolean) => void;
    ctx: AudioContext;
    /** `ctx.currentTime` when it started, and its length. */
    startedAt: number;
    durationSec: number;
  } | null = null;

  /** Call synchronously inside the click that starts the session. With
   *  `callSinkId` (Call mode), also ready the context that plays into the
   *  call's mic device. */
  unlock(
    callSinkId?: string | null,
    hearSinkId?: string | null,
    opts?: { boostHere?: number },
  ): void {
    // During a WhatsApp / Zoom / Teams call macOS turns other apps down
    // ("ducking"), and translations played here were hard to hear (DES
    // 03-10-2026): Call mode plays them louder, limited so they never clip.
    this.hereBoost = opts?.boostHere ?? 1;
    this.hereChain = null;
    if (!this.ctx || this.ctx.state === "closed") {
      this.ctx = new AudioContext();
      this.hearSinkId = "";
    }
    void this.ctx.resume().catch(() => undefined);
    this.setHearSink(hearSinkId ?? null);
    if (this.callCtx && this.callCtx.state !== "closed")
      void this.callCtx.close().catch(() => undefined);
    this.callCtx = null;
    if (callSinkId) {
      const callCtx = new AudioContext() as SinkableContext;
      void callCtx.resume().catch(() => undefined);
      // A device that cannot be opened leaves the translation here instead.
      void callCtx.setSinkId?.(callSinkId).catch(() => {
        if (this.callCtx === callCtx) this.callCtx = null;
        void callCtx.close().catch(() => undefined);
      });
      this.callCtx = callCtx;
    }
  }

  /** Decode and play; resolves when the audio has really finished (or `stop()`).
   *  `toCall`: into the call device (Call mode), when one is ready.
   *  `alsoHere`: with `toCall`, the same audio in your headphones too, in
   *  step, so you can check your own translation. */
  async play(
    source: TurnAudioSource,
    authToken: string | null,
    opts?: { toCall?: boolean; alsoHere?: boolean },
  ): Promise<PlayResult> {
    const ctx = opts?.toCall && this.callCtx ? this.callCtx : this.requireCtx();
    if (ctx.state === "suspended") await ctx.resume().catch(() => undefined);
    const bytes = await loadBytes(source, authToken);
    const buffer = await ctx.decodeAudioData(bytes);
    this.stop();
    // The copy for your ears (an AudioBuffer plays in any context).
    let echo: AudioBufferSourceNode | null = null;
    if (opts?.alsoHere && ctx === this.callCtx) {
      const here = this.requireCtx();
      if (here.state === "suspended") await here.resume().catch(() => undefined);
      echo = here.createBufferSource();
      echo.buffer = buffer;
      echo.connect(this.hereOut(here));
    }
    return new Promise<PlayResult>((resolve) => {
      const node = ctx.createBufferSource();
      node.buffer = buffer;
      node.connect(ctx === this.callCtx ? ctx.destination : this.hereOut(ctx));
      let done = false;
      const finish = (interrupted: boolean) => {
        if (done) return;
        done = true;
        if (this.current?.node === node) this.current = null;
        try {
          node.disconnect();
        } catch {
          /* ignore */
        }
        if (echo) {
          try {
            if (interrupted) echo.stop();
            echo.disconnect();
          } catch {
            /* ignore */
          }
        }
        resolve({ durationSec: buffer.duration, interrupted });
      };
      node.onended = () => finish(false);
      this.current = {
        node,
        finish,
        ctx,
        startedAt: ctx.currentTime,
        durationSec: buffer.duration,
      };
      node.start();
      echo?.start();
    });
  }

  /** Short "your turn" cue. Resolves when it has finished sounding. */
  playBeep(opts?: { toCall?: boolean }): Promise<void> {
    // `toCall`: into the call only (Call mode's "your turn" for them), as a
    // walkie-talkie "over" double beep, the one DES picked (04-10-2026): a
    // single soft ping was hard to notice through a call.
    if (opts?.toCall && this.callCtx) return this.playOverBeep(this.callCtx);
    const ctx = this.requireCtx();
    const t0 = ctx.currentTime + 0.01;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(0.18, t0 + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.14);
    osc.connect(gain);
    gain.connect(ctx.destination);
    return new Promise((resolve) => {
      osc.onended = () => {
        try {
          osc.disconnect();
          gain.disconnect();
        } catch {
          /* ignore */
        }
        resolve();
      };
      osc.start(t0);
      osc.stop(t0 + 0.15);
    });
  }

  /** Two short 1.2 kHz square beeps, 120 ms apart ("over"). */
  private playOverBeep(ctx: AudioContext): Promise<void> {
    const start = ctx.currentTime + 0.01;
    for (const at of [0, 0.12]) {
      const t0 = start + at;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.value = 1200;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.25, t0 + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.08);
      osc.connect(gain).connect(ctx.destination);
      osc.onended = () => {
        try {
          osc.disconnect();
          gain.disconnect();
        } catch {
          /* ignore */
        }
      };
      osc.start(t0);
      osc.stop(t0 + 0.1);
    }
    return new Promise((resolve) => setTimeout(resolve, 260));
  }

  /** Seconds into the playing translation and its length; null when nothing
   *  is sounding yet (still fetching or decoding) or it has ended. */
  progress(): PlaybackProgress | null {
    const cur = this.current;
    if (!cur) return null;
    return {
      currentSec: Math.max(0, cur.ctx.currentTime - cur.startedAt),
      durationSec: cur.durationSec,
    };
  }

  get isPlaying(): boolean {
    return this.current !== null;
  }

  stop(): void {
    const cur = this.current;
    if (!cur) return;
    this.current = null;
    try {
      cur.node.stop();
    } catch {
      /* not started or already ended */
    }
    cur.finish(true);
  }

  close(): void {
    this.stop();
    for (const ctx of [this.ctx, this.callCtx]) {
      if (ctx && ctx.state !== "closed") void ctx.close().catch(() => undefined);
    }
    this.ctx = null;
    this.callCtx = null;
  }

  /**
   * Where you hear translations (Call mode: your headphones); null = the
   * Mac's output. Also during a session, when headphones are plugged in or
   * out. A device that cannot be opened stays on the Mac's output.
   */
  setHearSink(hearSinkId: string | null): void {
    const ctx = this.ctx as SinkableContext | null;
    if (!ctx || ctx.state === "closed" || !ctx.setSinkId) return;
    if ((hearSinkId ?? "") === this.hearSinkId) return;
    this.hearSinkId = hearSinkId ?? "";
    void ctx.setSinkId(this.hearSinkId).catch(() => {
      this.hearSinkId = "";
    });
  }

  /** Where audio played here goes: straight out, or boosted and limited. */
  private hereOut(ctx: AudioContext): AudioNode {
    if (this.hereBoost <= 1) return ctx.destination;
    if (this.hereChain?.ctx === ctx) return this.hereChain.input;
    const gain = ctx.createGain();
    gain.gain.value = this.hereBoost;
    const limiter = ctx.createDynamicsCompressor();
    // Compress, so the boost makes speech denser and louder rather than
    // being pressed straight back down (speech TTS already peaks near 0 dB).
    limiter.threshold.value = -24;
    limiter.knee.value = 6;
    limiter.ratio.value = 8;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.15;
    // A hard ceiling after the compressor, so the boost never clips.
    const ceiling = ctx.createDynamicsCompressor();
    ceiling.threshold.value = -2;
    ceiling.knee.value = 0;
    ceiling.ratio.value = 20;
    ceiling.attack.value = 0.001;
    ceiling.release.value = 0.1;
    gain.connect(limiter).connect(ceiling).connect(ctx.destination);
    this.hereChain = { ctx, input: gain };
    return gain;
  }

  private requireCtx(): AudioContext {
    if (!this.ctx || this.ctx.state === "closed") {
      this.ctx = new AudioContext();
      this.hearSinkId = "";
    }
    return this.ctx;
  }
}

async function loadBytes(source: TurnAudioSource, authToken: string | null): Promise<ArrayBuffer> {
  if (source.kind === "inline") {
    const bin = atob(source.base64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
    return out.buffer;
  }
  const headers: Record<string, string> = {};
  if (source.kind === "proxy" && authToken) headers.Authorization = `Bearer ${authToken}`;
  const res = await fetch(source.url, { headers });
  if (!res.ok) throw new Error(`Could not load the translation audio (${res.status})`);
  return res.arrayBuffer();
}
